import React, { Component, useState } from 'react';

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const handle = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={handle}
      className={`btn btn-sm ${copied ? 'btn-success' : 'btn-outline-secondary'}`}
      style={{ position: 'absolute', top: 8, right: 8, fontSize: '0.7rem', padding: '2px 8px', opacity: 0.85 }}
    >
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
}

const DEFAULT_SERVER_LICENSE = '[your-server-license]';
const DEFAULT_AGENT_LICENSE  = '[your-agent-license]';

export default class Deploy extends Component {
  constructor(props) {
    super(props);
    this.state = {
      topTab:       'server', // 'server' | 'agent'
      serverAction: 'run',    // 'run' | 'install' | 'uninstall'
      serverOS:     'linux',  // 'linux' | 'macos' | 'windows'
      agentAction:  'run',
      agentOS:      'linux',
    };
  }

  render() {
    const sl = this.props.server_license || DEFAULT_SERVER_LICENSE;
    const al = this.props.agent_license  || DEFAULT_AGENT_LICENSE;
    const { topTab } = this.state;

    return (
      <div className="modal fade" id="deployModal" tabIndex="-1" aria-labelledby="deployModalLabel" aria-hidden="true">
        <div className="modal-dialog modal-xl modal-dialog-scrollable">
          <div className="modal-content bg-dark text-light border-secondary">
            <div className="modal-header border-secondary">
              <h5 className="modal-title" id="deployModalLabel">Deployment Guide</h5>
              <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" aria-label="Close" />
            </div>

            <div className="modal-body text-start">
              {/* Top-level: IR Server / Agent */}
              <ul className="nav nav-tabs mb-4">
                {[['server', 'IR Server'], ['agent', 'Agent']].map(([key, label]) => (
                  <li className="nav-item" key={key}>
                    <button
                      className={`nav-link${topTab === key ? ' active' : ''}`}
                      onClick={() => this.setState({ topTab: key })}
                    >{label}</button>
                  </li>
                ))}
              </ul>

              {topTab === 'server' && this.renderServer(sl)}
              {topTab === 'agent'  && this.renderAgent(al)}
            </div>

            <div className="modal-footer border-secondary">
              <button type="button" className="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── shared helpers ────────────────────────────────────────────────────────────

  codeBlock(content, color = 'text-light') {
    return (
      <div style={{ position: 'relative' }}>
        <CopyButton text={content} />
        <pre className={`bg-black ${color} p-3 pe-5 rounded small mb-0`} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
          <code>{content}</code>
        </pre>
      </div>
    );
  }

  actionTabs(current, onChange) {
    return (
      <ul className="nav nav-pills mb-3">
        {[['run', 'Run'], ['install', 'Install (autostart)'], ['uninstall', 'Uninstall']].map(([key, label]) => (
          <li className="nav-item" key={key}>
            <button
              className={`nav-link${current === key ? ' active' : ''}`}
              onClick={() => onChange(key)}
            >{label}</button>
          </li>
        ))}
      </ul>
    );
  }

  osTabs(current, onChange) {
    return (
      <ul className="nav nav-pills nav-fill mb-3" style={{ gap: 4 }}>
        {[['linux', 'Linux'], ['macos', 'macOS'], ['windows', 'Windows']].map(([key, label]) => (
          <li className="nav-item" key={key}>
            <button
              className={`nav-link btn-sm${current === key ? ' active' : ' text-secondary'}`}
              style={{ border: '1px solid rgba(255,255,255,0.15)' }}
              onClick={() => onChange(key)}
            >{label}</button>
          </li>
        ))}
      </ul>
    );
  }

  // ── IR Server ─────────────────────────────────────────────────────────────────

  renderServer(sl) {
    const { serverAction, serverOS } = this.state;
    const set = (k, v) => this.setState({ [k]: v });

    return (
      <div>
        <p className="text-secondary small mb-3">
          The IR Server is the central hub. Deploy it on any machine your analysts can reach:
          a cloud VM, an on-prem box, or your laptop.&nbsp;
          Server license: <code className="text-warning">{sl}</code>
        </p>

        {this.actionTabs(serverAction, v => set('serverAction', v))}

        {serverAction === 'run'       && this.renderServerRun(sl, serverOS,   v => set('serverOS', v))}
        {serverAction === 'install'   && this.renderServerInstall(sl, serverOS, v => set('serverOS', v))}
        {serverAction === 'uninstall' && this.renderServerUninstall(serverOS,   v => set('serverOS', v))}
      </div>
    );
  }

  renderServerRun(sl, os, setOS) {
    const cmds = {
      linux:   `FS_LICENSE_KEY="${sl}" ./fs-server --public-hostname ir.yourcompany.com --tls-cert ./fullchain.pem --tls-key ./privkey.pem --storage ./evidence`,
      macos:   `FS_LICENSE_KEY="${sl}" ./fs-server --public-hostname ir.yourcompany.com --tls-cert ./fullchain.pem --tls-key ./privkey.pem --storage ./evidence`,
      windows: `$env:FS_LICENSE_KEY="${sl}"; .\\fs-server.exe --public-hostname ir.yourcompany.com --tls-cert .\\fullchain.pem --tls-key .\\privkey.pem --storage .\\evidence`,
    };
    return (
      <div>
        <p className="text-secondary small mb-3">
          Run the server interactively. Stop it with <code>Ctrl-C</code>.
          Use <strong>Install (autostart)</strong> for persistent deployment.
        </p>
        {this.osTabs(os, setOS)}
        {this.codeBlock(cmds[os], 'text-success')}
        <p className="text-secondary small mt-2 mb-0">
          <code>--public-hostname</code> (the DNS name agents will dial) and <code>--tls-cert</code>/<code>--tls-key</code>
          are required: the server only speaks TLS, there is no plaintext mode. Set <code>FS_UI_PASSWORD=YourPassword</code> to
          require analysts to log in. <code>--port</code> (default 8443) serves the dashboard and agent API; <code>--tunnel-port</code>
          (default 8444) is the reversed-tunnel listener agents use for drive/acquire commands. Both ports must be directly
          reachable from agent endpoints — unlike the license server, this one cannot sit behind an HTTP proxy or CDN such as
          Cloudflare's proxy mode, since the tunnel relies on a persistent raw TLS connection, not ordinary request/response traffic.
        </p>
      </div>
    );
  }

  renderServerInstall(sl, os, setOS) {
    const linuxService = `[Unit]
Description=ForeignSix IR Server
After=network-online.target
Wants=network-online.target

[Service]
User=foreignsix
Group=foreignsix
WorkingDirectory=/opt/foreignsix/bin
Environment=FS_LICENSE_KEY=${sl}
Environment=FS_PUBLIC_HOSTNAME=ir.yourcompany.com
Environment=FS_UI_PASSWORD=YourPassword
Environment=FS_STORAGE_DIR=/opt/foreignsix/evidence
ExecStart=/opt/foreignsix/bin/fs-server --tls-cert /etc/ssl/ir.yourcompany.com.pem --tls-key /etc/ssl/ir.yourcompany.com.key
Restart=always
RestartSec=3
StandardOutput=journal
StandardError=journal

[Install]
WantedBy=multi-user.target`;

    const linuxManual = `# Copy the binary
sudo mkdir -p /opt/foreignsix/evidence
sudo cp fs-server /opt/foreignsix/bin/
sudo chmod 755 /opt/foreignsix/bin/fs-server
sudo chown -R foreignsix:foreignsix /opt/foreignsix/bin/fs-server

# Save the unit file above to /etc/systemd/system/fs-server.service, then:
sudo systemctl daemon-reload
sudo systemctl enable --now fs-server

# Verify
sudo systemctl status fs-server
sudo journalctl -u fs-server -f`;

    const macosPlist = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN"
  "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
    <key>Label</key>
    <string>com.foreignsix.server</string>
    <key>ProgramArguments</key>
    <array>
        <string>/usr/local/bin/fs-server</string>
        <string>--tls-cert</string>
        <string>/usr/local/etc/foreignsix/ir.yourcompany.com.pem</string>
        <string>--tls-key</string>
        <string>/usr/local/etc/foreignsix/ir.yourcompany.com.key</string>
    </array>
    <key>EnvironmentVariables</key>
    <dict>
        <key>FS_LICENSE_KEY</key>
        <string>${sl}</string>
        <key>FS_PUBLIC_HOSTNAME</key>
        <string>ir.yourcompany.com</string>
        <key>FS_UI_PASSWORD</key>
        <string>YourPassword</string>
        <key>FS_STORAGE_DIR</key>
        <string>/usr/local/var/foreignsix/evidence</string>
    </dict>
    <key>RunAtLoad</key>
    <true/>
    <key>KeepAlive</key>
    <true/>
    <key>StandardOutPath</key>
    <string>/var/log/fs-server.log</string>
    <key>StandardErrorPath</key>
    <string>/var/log/fs-server.log</string>
</dict>
</plist>`;

    const macosManual = `sudo cp fs-server /usr/local/bin/fs-server
sudo chmod 755 /usr/local/bin/fs-server
sudo mkdir -p /usr/local/var/foreignsix/evidence

# Save the plist above to /Library/LaunchDaemons/com.foreignsix.server.plist, then:
sudo launchctl load -w /Library/LaunchDaemons/com.foreignsix.server.plist

# Verify
sudo launchctl list | grep foreignsix
tail -f /var/log/fs-server.log`;

    const winCopy = `# Run as Administrator in PowerShell
New-Item -ItemType Directory -Force -Path "C:\\Program Files\\ForeignSix"
New-Item -ItemType Directory -Force -Path "C:\\Program Files\\ForeignSix\\evidence"
Copy-Item fs-server.exe "C:\\Program Files\\ForeignSix\\fs-server.exe"`;

    const winService = `sc.exe create "FsServer" \`
    binPath= "\\"C:\\Program Files\\ForeignSix\\fs-server.exe\\" --tls-cert \\"C:\\Program Files\\ForeignSix\\ir.yourcompany.com.pem\\" --tls-key \\"C:\\Program Files\\ForeignSix\\ir.yourcompany.com.key\\"" \`
    start= auto \`
    DisplayName= "ForeignSix IR Server"
sc.exe description "FsServer" "ForeignSix IR server"
$regPath = "HKLM:\\SYSTEM\\CurrentControlSet\\Services\\FsServer"
New-ItemProperty -Path $regPath -Name "Environment" -Value @("FS_LICENSE_KEY=${sl}", "FS_PUBLIC_HOSTNAME=ir.yourcompany.com", "FS_UI_PASSWORD=YourPassword", "FS_STORAGE_DIR=C:\\Program Files\\ForeignSix\\evidence") -PropertyType MultiString -Force
sc.exe start "FsServer"

# Verify
sc.exe query "FsServer"`;

    return (
      <div>
        <p className="text-secondary small mb-3">
          Deploy with autostart so the server survives reboots. <strong className="text-warning">Never
          commit the license key to version control</strong>; use your secrets manager instead.
        </p>

        {this.osTabs(os, setOS)}

        {os === 'linux' && (
          <div>
            <h6>1. systemd unit file
              <small className="text-secondary ms-2 fw-normal">/etc/systemd/system/fs-server.service</small>
            </h6>
            {this.codeBlock(linuxService)}
            <h6 className="mt-4">2. Manual install</h6>
            {this.codeBlock(linuxManual, 'text-success')}
            <p className="text-secondary small mt-2 mb-0">
              Replace <code>YourPassword</code> with a strong password or pull it from your vault at deploy time.
              Logs are available with <code>journalctl -u fs-server</code>.
            </p>
          </div>
        )}

        {os === 'macos' && (
          <div>
            <h6>1. LaunchDaemon plist
              <small className="text-secondary ms-2 fw-normal">/Library/LaunchDaemons/com.foreignsix.server.plist</small>
            </h6>
            {this.codeBlock(macosPlist)}
            <h6 className="mt-4">2. Manual install</h6>
            {this.codeBlock(macosManual, 'text-success')}
          </div>
        )}

        {os === 'windows' && (
          <div>
            <h6>1. Copy binary</h6>
            {this.codeBlock(winCopy, 'text-success')}
            <h6 className="mt-4">2. Register as Windows Service
              <small className="text-secondary ms-2 fw-normal">PowerShell (run as Administrator)</small>
            </h6>
            {this.codeBlock(winService, 'text-success')}
            <p className="text-secondary small mt-2 mb-0">
              For richer service management (log rotation, restart policies) use{' '}
              <strong>NSSM</strong>: <code>nssm install FsServer</code>, then <code>nssm set FsServer AppEnvironmentExtra FS_LICENSE_KEY=…</code>.
              Replace <code>YourPassword</code> and the storage path before deploying.
            </p>
          </div>
        )}

        <hr className="border-secondary mt-4" />
        <h6>Updating the server</h6>
        <table className="table table-dark table-sm small mb-0">
          <thead><tr><th>Platform</th><th>Update approach</th></tr></thead>
          <tbody>
            <tr><td>Linux</td><td>Replace binary → <code>sudo systemctl restart fs-server</code></td></tr>
            <tr><td>macOS</td><td>Replace binary → <code>sudo launchctl unload …plist && sudo launchctl load -w …plist</code></td></tr>
            <tr><td>Windows</td><td><code>sc.exe stop FsServer</code> → replace binary → <code>sc.exe start FsServer</code></td></tr>
          </tbody>
        </table>
      </div>
    );
  }

  renderServerUninstall(os, setOS) {
    const cmds = {
      linux: `sudo systemctl stop fs-server
sudo systemctl disable fs-server
sudo rm /etc/systemd/system/fs-server.service
sudo systemctl daemon-reload
sudo rm /opt/foreignsix/bin/fs-server`,
      macos: `sudo launchctl unload -w /Library/LaunchDaemons/com.foreignsix.server.plist
sudo rm /Library/LaunchDaemons/com.foreignsix.server.plist
sudo rm /usr/local/bin/fs-server`,
      windows: `sc.exe stop "FsServer"
sc.exe delete "FsServer"
Remove-Item "C:\\Program Files\\ForeignSix\\fs-server.exe" -Force`,
    };
    return (
      <div>
        <p className="text-secondary small mb-3">
          Stops and removes the server service. Evidence stored in the storage directory is not deleted.
        </p>
        {this.osTabs(os, setOS)}
        {this.codeBlock(cmds[os], 'text-danger')}
      </div>
    );
  }

  // ── Agent ─────────────────────────────────────────────────────────────────────

  renderAgent(al) {
    const { agentAction, agentOS } = this.state;
    const set = (k, v) => this.setState({ [k]: v });

    return (
      <div>
        <p className="text-secondary small mb-3">
          The agent runs on each endpoint you want to acquire from. Deploy it via your
          organisation's endpoint management tooling for fleet-scale rollout.&nbsp;
          Agent license: <code className="text-warning">{al}</code>
        </p>

        {this.actionTabs(agentAction, v => set('agentAction', v))}

        {agentAction === 'run'       && this.renderAgentRun(al, agentOS,   v => set('agentOS', v))}
        {agentAction === 'install'   && this.renderAgentInstall(al, agentOS, v => set('agentOS', v))}
        {agentAction === 'uninstall' && this.renderAgentUninstall(agentOS,   v => set('agentOS', v))}
      </div>
    );
  }

  renderAgentRun(al, os, setOS) {
    const cmds = {
      linux:   `FS_LICENSE_KEY="${al}" ./fs-agent`,
      macos:   `FS_LICENSE_KEY="${al}" ./fs-agent`,
      windows: `$env:FS_LICENSE_KEY="${al}"; .\\fs-agent.exe`,
    };
    return (
      <div>
        <p className="text-secondary small mb-3">
          Run the agent directly for a one-off acquisition. The agent stops when the process exits.
          Use <strong>Install (autostart)</strong> for persistent fleet deployment.
        </p>
        {this.osTabs(os, setOS)}
        {this.codeBlock(cmds[os], 'text-success')}
      </div>
    );
  }

  renderAgentInstall(al, os, setOS) {
    const linuxService = `[Unit]
Description=ForeignSix Agent
After=network-online.target
Wants=network-online.target

[Service]
Environment=FS_LICENSE_KEY=${al}
ExecStart=/usr/local/bin/fs-agent
Restart=on-failure
RestartSec=15
User=root
StandardOutput=journal
StandardError=journal

[Install]
WantedBy=multi-user.target`;

    const linuxManual = `sudo cp fs-agent /usr/local/bin/fs-agent
sudo chmod 755 /usr/local/bin/fs-agent
# Save the unit file above to /etc/systemd/system/fs-agent.service, then:
sudo systemctl daemon-reload
sudo systemctl enable --now fs-agent`;

    const ansiblePlaybook = `- hosts: targets
  become: true
  vars:
    license_key: "{{ vault_license_key }}"
  tasks:
    - name: Copy agent binary
      copy:
        src: fs-agent
        dest: /usr/local/bin/fs-agent
        mode: '0755'
        owner: root

    - name: Install systemd unit
      template:
        src: fs-agent.service.j2
        dest: /etc/systemd/system/fs-agent.service
      notify: Reload and restart agent

  handlers:
    - name: Reload and restart agent
      systemd:
        name: fs-agent
        daemon_reload: true
        enabled: true
        state: restarted`;

    const macosPlist = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE plist PUBLIC "-//Apple//DTD PLIST 1.0//EN"
  "http://www.apple.com/DTDs/PropertyList-1.0.dtd">
<plist version="1.0">
<dict>
    <key>Label</key>
    <string>com.foreignsix.agent</string>
    <key>ProgramArguments</key>
    <array>
        <string>/usr/local/bin/fs-agent</string>
    </array>
    <key>EnvironmentVariables</key>
    <dict>
        <key>FS_LICENSE_KEY</key>
        <string>${al}</string>
    </dict>
    <key>RunAtLoad</key>
    <true/>
    <key>KeepAlive</key>
    <true/>
    <key>StandardOutPath</key>
    <string>/var/log/fs-agent.log</string>
    <key>StandardErrorPath</key>
    <string>/var/log/fs-agent.log</string>
</dict>
</plist>`;

    const macosManual = `sudo cp fs-agent /usr/local/bin/fs-agent
sudo chmod 755 /usr/local/bin/fs-agent
# Save the plist above to /Library/LaunchDaemons/com.foreignsix.agent.plist, then:
sudo launchctl load -w /Library/LaunchDaemons/com.foreignsix.agent.plist`;

    const winCopy = `# Run as Administrator in PowerShell
New-Item -ItemType Directory -Force -Path "C:\\Program Files\\ForeignSix"
Copy-Item fs-agent.exe "C:\\Program Files\\ForeignSix\\fs-agent.exe"`;

    const winService = `sc.exe create "ForeignSixAgent" \`
    binPath= "C:\\Program Files\\ForeignSix\\fs-agent.exe" \`
    start= auto \`
    DisplayName= "ForeignSix Agent"
sc.exe description "ForeignSixAgent" "ForeignSix forensic acquisition agent"
$regPath = "HKLM:\\SYSTEM\\CurrentControlSet\\Services\\ForeignSixAgent"
New-ItemProperty -Path $regPath -Name "Environment" -Value @("FS_LICENSE_KEY=${al}") -PropertyType MultiString -Force
sc.exe start "ForeignSixAgent"`;

    return (
      <div>
        <p className="text-secondary small mb-3">
          Deploy via your organisation's endpoint management tooling to ensure autostart at boot
          and a proper audit trail. <strong className="text-warning">Never embed the license key
          in version-controlled files</strong>; use your secrets manager or vault instead.
        </p>

        {this.osTabs(os, setOS)}

        {os === 'linux' && (
          <div>
            <h6>1. systemd unit file
              <small className="text-secondary ms-2 fw-normal">/etc/systemd/system/fs-agent.service</small>
            </h6>
            {this.codeBlock(linuxService)}
            <h6 className="mt-4">2. Manual install</h6>
            {this.codeBlock(linuxManual, 'text-success')}
            <h6 className="mt-4">Ansible playbook <small className="text-secondary fw-normal">(recommended for fleet)</small></h6>
            {this.codeBlock(ansiblePlaybook)}
            <p className="text-secondary small mt-2 mb-0">
              Store the license key with <code>ansible-vault encrypt_string</code>.
              Chef and Puppet users should use the equivalent <code>template</code> and <code>service</code> resources.
            </p>
          </div>
        )}

        {os === 'macos' && (
          <div>
            <h6>1. LaunchDaemon plist
              <small className="text-secondary ms-2 fw-normal">/Library/LaunchDaemons/com.foreignsix.agent.plist</small>
            </h6>
            {this.codeBlock(macosPlist)}
            <h6 className="mt-4">2. Manual install</h6>
            {this.codeBlock(macosManual, 'text-success')}
            <h6 className="mt-4">Jamf Pro</h6>
            <ol className="small text-secondary">
              <li>Package the binary and plist into a <code>.pkg</code> using <strong>Packages.app</strong> or <code>pkgbuild</code></li>
              <li>Upload to Jamf Pro → <strong>Packages</strong></li>
              <li>Create a <strong>Policy</strong>: Packages → add your <code>.pkg</code>, Trigger: <em>Enrollment Complete</em> + <em>Recurring Check-in</em></li>
              <li>Scope to the target computer group</li>
              <li>Add a Jamf <strong>Script</strong> (after) to call:
                <code className="d-block mt-1 ms-2">launchctl load -w /Library/LaunchDaemons/com.foreignsix.agent.plist</code>
              </li>
            </ol>
            <p className="text-secondary small mb-0">
              Store the license key as a Jamf parameter or pull it from your secrets manager at install time.
            </p>
          </div>
        )}

        {os === 'windows' && (
          <div>
            <h6>1. Copy binary</h6>
            {this.codeBlock(winCopy, 'text-success')}
            <h6 className="mt-4">2. Register as Windows Service
              <small className="text-secondary ms-2 fw-normal">PowerShell (run as Administrator)</small>
            </h6>
            {this.codeBlock(winService, 'text-success')}
            <p className="text-secondary small mt-2">
              For more robust service management use <strong>NSSM</strong>: <code>nssm install ForeignSixAgent</code>.
            </p>
            <h6 className="mt-3">Intune Win32 App</h6>
            <ol className="small text-secondary">
              <li>Wrap the installer script into a <code>.intunewin</code> package using the <strong>Microsoft Win32 Content Prep Tool</strong></li>
              <li>Upload to Intune → Apps → Windows → Add (Win32)</li>
              <li>Install command: <code>powershell.exe -ExecutionPolicy Bypass -File install-agent.ps1</code></li>
              <li>Detection rule: service <code>ForeignSixAgent</code> exists with state <em>Running</em></li>
              <li>Assign to a Device Group</li>
            </ol>
            <p className="text-secondary small mb-0">
              For SCCM: create an Application with the same install script, deploy to a Device Collection.
              Store the license key in an Intune Configuration Profile or Azure Key Vault.
            </p>
          </div>
        )}

        <hr className="border-secondary mt-4" />
        <h6>Updating the agent</h6>
        <table className="table table-dark table-sm small mb-1">
          <thead><tr><th>Platform</th><th>Update approach</th></tr></thead>
          <tbody>
            <tr><td>Linux</td><td>Replace binary → <code>sudo systemctl restart fs-agent</code></td></tr>
            <tr><td>macOS</td><td>Replace binary → <code>sudo launchctl unload …plist && sudo launchctl load -w …plist</code></td></tr>
            <tr><td>Windows</td><td><code>sc.exe stop ForeignSixAgent</code> → replace binary → <code>sc.exe start ForeignSixAgent</code></td></tr>
          </tbody>
        </table>
        <p className="text-secondary small mb-0">
          Your Ansible playbook / Jamf policy / Intune app handles this automatically on the next
          check-in when you bump the package version.
        </p>
      </div>
    );
  }

  renderAgentUninstall(os, setOS) {
    const cmds = {
      linux: `sudo systemctl stop fs-agent
sudo systemctl disable fs-agent
sudo rm /etc/systemd/system/fs-agent.service
sudo systemctl daemon-reload
sudo rm /usr/local/bin/fs-agent`,
      macos: `sudo launchctl unload -w /Library/LaunchDaemons/com.foreignsix.agent.plist
sudo rm /Library/LaunchDaemons/com.foreignsix.agent.plist
sudo rm /usr/local/bin/fs-agent`,
      windows: `sc.exe stop "ForeignSixAgent"
sc.exe delete "ForeignSixAgent"
Remove-Item "C:\\Program Files\\ForeignSix\\fs-agent.exe" -Force
Remove-Item "C:\\Program Files\\ForeignSix" -Force`,
    };
    return (
      <div>
        <p className="text-secondary small mb-3">
          Run on the endpoint to stop, disable, and remove the agent.
          If deployed via MDM, also remove the agent from the relevant policy/app assignment
          in Jamf / Intune / SCCM to prevent re-deployment.
        </p>
        {this.osTabs(os, setOS)}
        {this.codeBlock(cmds[os], 'text-danger')}
      </div>
    );
  }
}
